/**
 * CareSeekerMatchmaking Component
 *
 * CRITICAL: This is a COMPLETELY SEPARATE component from ProDashboardMatchmaking.
 * It is NOT a conditional render and has ZERO FA dependencies.
 *
 * This component provides Care Seekers with AI-powered matchmaking to find
 * service providers. It visually matches the FA matchmaking page but operates
 * independently with no client management or advisor validation logic.
 */

import { useRef, useEffect } from 'react';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useMatchmakingStateManagement } from '../hooks/useMatchmakingStateManagement';
import { useMatchmakingState } from '../hooks/useMatchmakingState';
import { useLoadingMessages } from '../hooks/useLoadingMessages';
import { useImagePreloading } from '../hooks/useImagePreloading';
import { useMatchmakingComputed } from '../hooks/useMatchmakingComputed';
import { useCreateSearch, useFilteredProviders, useSaveSearchResults } from '../hooks/useMatchmaking';
import { useMatchSearch } from '../hooks/useMatchSearch';
import { GENERIC_SEARCH_ID } from '../constants/client-selection';
import { MatchmakingSearchInput } from './ProDashboardMatchmaking/MatchmakingSearchInput';
import { MatchmakingEmptyState } from './ProDashboardMatchmaking/MatchmakingEmptyState';
import { MatchmakingNoResultsState } from './ProDashboardMatchmaking/MatchmakingNoResultsState';
import { MatchmakingResultsSection } from './ProDashboardMatchmaking/MatchmakingResultsSection';
import { ProDashboardMatchmakingModals } from './ProDashboardMatchmakingModals';
import type { HistoryItem } from './MatchmakingHistoryPopover';
import type { StepperAnswers } from '../hooks/useMatchmakingStateManagement';
import { BetaBanner } from '@/shared/components/BetaBanner';

/**
 * Care Seeker Matchmaking Component
 *
 * Key differences from FA component:
 * - Uses user.id as search context (not client IDs)
 * - No client management or advisor validation
 * - No tabs (only single search interface)
 * - No client switcher header
 * - Uses CARE_SEEKER userType for API calls
 */
export const CareSeekerMatchmaking = () => {
  const { user: loggedInUser } = useAuth();

  // State management - same as FA but no client context
  const state = useMatchmakingStateManagement();
  const {
    isLoading,
    setIsLoading,
    isLoadingMore,
    setIsLoadingMore,
    showNoProvidersFound,
    setShowNoProvidersFound,
    noProviderMessage,
    setNoProviderMessage,
    loadMoreMessage,
    setLoadMoreMessage,
    isHistoryPopoverOpen,
    setHistoryPopoverOpen,
    showHistoryTooltip,
    setShowHistoryTooltip,
    isViewingHistoryResults,
    setIsViewingHistoryResults,
    shouldFetchHistory,
    setShouldFetchHistory,
    isSupportModalOpen,
    setIsSupportModalOpen,
    isHistoryDetailsModalOpen,
    setIsHistoryDetailsModalOpen,
    currentSearchId,
    setCurrentSearchId,
    loadCount,
    setLoadCount,
    selectedZipCode,
    selectedCounty,
    selectedAddress,
    setSelectedAddress,
    selectedLatitude,
    selectedLongitude,
    selectedState,
    searchCounter,
    setSearchCounter,
    stepperAnswers,
    setStepperAnswers,
    lastPromptRef,
    lastHistoryItemRef,
    setError,
    isLocationModalOpen,
    setIsLocationModalOpen,
    addressResetTrigger,
  } = state;

  // FEATURE 025: Care Seekers use GENERIC_SEARCH_ID (generic searches with no client)
  // advisorId will be the Care Seeker's user ID
  const advisorId = loggedInUser?.id || null;

  // Matchmaking state with Care Seeker context (using GENERIC_SEARCH_ID)
  const matchmakingState = useMatchmakingState(GENERIC_SEARCH_ID);
  const matchingPrompt = matchmakingState.query;
  const apiResults = matchmakingState.matches;
  const hasMore = matchmakingState.hasMore;

  // Loading messages
  const { currentLoadingMessageIndex, currentLoadingTheme } = useLoadingMessages(isLoading);

  // Image preloading
  const {
    isPreloadingImages,
    preloadedImages,
    imagePreloadingMessageIndex,
    currentImagePreloadingTheme,
  } = useImagePreloading(apiResults, isLoading);

  // Computed values
  const {
    matchResults,
    hasMatches,
    shouldShowPostSingleLoadExhaustedMessage,
    shouldShowPostSecondLoadMessage,
    cheerfulMessage,
  } = useMatchmakingComputed({
    selectedClient: null, // No client context for Care Seekers
    selectedZipCode,
    selectedCounty,
    isViewingHistoryResults,
    searchCounter,
    isLoading,
    hasMore,
    loadCount,
    apiResults,
  });

  // API mutations
  const createSearchMutation = useCreateSearch();
  const filteredProvidersMutation = useFilteredProviders();
  const saveResultsMutation = useSaveSearchResults();

  // Ref for address clearing
  const userClearedAddressRef = useRef(false);

  // Address geocoding handler (accepts GeocodedAddressData)
  const handleAddressSelectAndGeocode = (data: any) => {
    if (!data.description || data.description.trim() === '') {
      userClearedAddressRef.current = true;
      setSelectedAddress('');
      state.setSelectedLatitude(null);
      state.setSelectedLongitude(null);
      state.setSelectedState('');
      state.setSelectedCounty('');
      state.setSelectedZipCode('');
      return;
    }

    userClearedAddressRef.current = false;
    setSelectedAddress(data.description);
    state.setSelectedLatitude(data.latitude);
    state.setSelectedLongitude(data.longitude);
    state.setSelectedState(data.state || '');
    state.setSelectedCounty(data.county || '');
    state.setSelectedZipCode(data.zipCode || '');
  };

  // Setup executeMatchSearch for Care Seeker
  const { executeMatchSearch } = useMatchSearch({
    matchingPrompt,
    hasClients: true, // Always true for Care Seekers (searching for themselves)
    selectedClientId: GENERIC_SEARCH_ID, // FEATURE 025: Use GENERIC_SEARCH_ID for generic searches
    advisorProfileId: advisorId, // Use Care Seeker's user ID as advisor
    stepperAnswers,
    apiResults,
    currentSearchId,
    loadCount,
    selectedState,
    selectedCounty,
    selectedAddress,
    selectedZipCode: selectedZipCode || '',
    selectedLatitude,
    selectedLongitude,
    clientProfile: undefined, // No client profile
    userType: 'CARE_SEEKER', // FEATURE 025: Routes to generic search endpoint
    matchmakingState,
    isViewingHistoryResults,
    createSearchMutation,
    filteredProvidersMutation,
    saveResultsMutation,
    setIsLoading,
    setIsLoadingMore,
    setError,
    setShowNoProvidersFound,
    setNoProviderMessage,
    setLoadMoreMessage,
    setIsLocationModalOpen,
    setCurrentSearchId,
    setLoadCount,
    setSearchCounter,
    setIsViewingHistoryResults,
    setShouldFetchHistory,
    lastPromptRef,
  });

  // Handle history selection
  const handleSelectHistory = (item: HistoryItem) => {
    matchmakingState.setClientQuery(item.promptText);
  };

  // Handle prompt changes
  const handlePromptChange = (value: string) => {
    matchmakingState.setClientQuery(value);
  };

  // Handle generate matches - opens location modal
  const handleGenerateMatches = () => {
    if (!matchingPrompt.trim()) {
      return;
    }
    lastPromptRef.current = matchingPrompt;
    setIsLocationModalOpen(true);
  };

  // Handle find matches - executes search
  const handleFindMatchesInternal = useRef((answers: StepperAnswers) => {
    setStepperAnswers(answers);
    setIsLocationModalOpen(false);
    executeMatchSearch(false, answers);
  });

  // Update the ref whenever dependencies change
  useEffect(() => {
    handleFindMatchesInternal.current = (answers: StepperAnswers) => {
      setStepperAnswers(answers);
      setIsLocationModalOpen(false);
      executeMatchSearch(false, answers);
    };
  }, [executeMatchSearch, setStepperAnswers, setIsLocationModalOpen]);

  // Handle load more
  const handleLoadMore = () => {
    executeMatchSearch(true, stepperAnswers);
  };

  // Modal props for support and history
  const modalProps = {
    advisorEmail: loggedInUser?.email || '',
    advisorName: `${loggedInUser?.firstName || ''} ${loggedInUser?.lastName || ''}`.trim(),
    searchQuery: matchingPrompt,
    locationZipCode: selectedZipCode,
    locationState: selectedState,
    locationCounty: selectedCounty,
    locationAddress: selectedAddress,
  };

  if (!loggedInUser) {
    return <h3>Please log in to use matchmaking.</h3>;
  }

  return (
    <div className="container mx-auto min-h-screen flex flex-col py-8 px-4 bg-background">
      <main className="w-full space-y-8 flex-1 min-w-0">
        {/* Header - Care Seeker specific */}
        <div className="space-y-2">
          <h2 className="text-2xl font-bold text-gray-900">bQuest Navigator</h2>
          <BetaBanner className="mb-4" />
          <p className="text-gray-600">
            Describe your care needs and let our AI find the best service providers for you.
          </p>
        </div>

        {/* Search Input */}
        <MatchmakingSearchInput
          matchingPrompt={matchingPrompt}
          onPromptChange={handlePromptChange}
          onGenerateMatches={handleGenerateMatches}
          isLoading={isLoading}
          hasClients={true}
          isHistoryPopoverOpen={isHistoryPopoverOpen}
          setHistoryPopoverOpen={setHistoryPopoverOpen}
          showHistoryTooltip={showHistoryTooltip}
          setShowHistoryTooltip={setShowHistoryTooltip}
          onSelectHistory={handleSelectHistory}
          advisorId={advisorId} // Use Care Seeker's user ID as advisor
          clientId={GENERIC_SEARCH_ID} // FEATURE 025: Use GENERIC_SEARCH_ID for history
          shouldFetchHistory={shouldFetchHistory && !!advisorId}
          onFetchHistoryComplete={() => setShouldFetchHistory(false)}
        />

        {/* Empty State */}
        {!isLoading && !hasMatches && !showNoProvidersFound && (
          <MatchmakingEmptyState />
        )}

        {/* No Results State */}
        {showNoProvidersFound && noProviderMessage && (
          <MatchmakingNoResultsState />
        )}

        {/* Results Section */}
        <MatchmakingResultsSection
          isLoading={isLoading}
          hasMatches={hasMatches}
          showNoProvidersFound={showNoProvidersFound}
          isPreloadingImages={isPreloadingImages}
          currentLoadingTheme={currentLoadingTheme}
          currentLoadingMessageIndex={currentLoadingMessageIndex}
          currentImagePreloadingTheme={currentImagePreloadingTheme}
          imagePreloadingMessageIndex={imagePreloadingMessageIndex}
          matchResults={matchResults}
          preloadedImages={preloadedImages}
          currentSearchId={currentSearchId}
          advisorId={advisorId} // Use Care Seeker's user ID as advisor
          clientId={GENERIC_SEARCH_ID} // FEATURE 025: Use GENERIC_SEARCH_ID for generic search
          isViewingHistoryResults={isViewingHistoryResults}
          onHistoryDetailsClick={() => setIsHistoryDetailsModalOpen(true)}
          cheerfulMessage={cheerfulMessage}
          shouldShowPostSingleLoadExhaustedMessage={shouldShowPostSingleLoadExhaustedMessage}
          shouldShowPostSecondLoadMessage={shouldShowPostSecondLoadMessage}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          loadMoreMessage={loadMoreMessage}
          onLoadMore={handleLoadMore}
          onContactUs={() => setIsSupportModalOpen(true)}
        />
      </main>

      {/* Modals */}
      <ProDashboardMatchmakingModals
        isClientModalOpen={false} // Never show client modal for Care Seekers
        setClientModalOpen={() => {}} // No-op
        clientProfile={null}
        clientAvatar=""
        ageDisplay=""
        isLocationModalOpen={isLocationModalOpen}
        setIsLocationModalOpen={setIsLocationModalOpen}
        userClearedAddressRef={userClearedAddressRef}
        onFindMatches={handleFindMatchesInternal.current}
        selectedAddress={selectedAddress}
        setSelectedAddress={setSelectedAddress}
        onAddressSelectAndGeocode={handleAddressSelectAndGeocode}
        addressResetTrigger={addressResetTrigger}
        selectedState={selectedState}
        selectedCounty={selectedCounty}
        selectedZipCode={selectedZipCode}
        selectedLatitude={selectedLatitude}
        selectedLongitude={selectedLongitude}
        stepperAnswers={stepperAnswers}
        setStepperAnswers={setStepperAnswers}
        isSupportModalOpen={isSupportModalOpen}
        setIsSupportModalOpen={setIsSupportModalOpen}
        advisorEmail={modalProps.advisorEmail}
        advisorName={modalProps.advisorName}
        searchQuery={modalProps.searchQuery}
        locationZipCode={modalProps.locationZipCode}
        locationState={modalProps.locationState}
        locationCounty={modalProps.locationCounty}
        locationAddress={modalProps.locationAddress}
        isHistoryDetailsModalOpen={isHistoryDetailsModalOpen}
        setIsHistoryDetailsModalOpen={setIsHistoryDetailsModalOpen}
        historyItem={lastHistoryItemRef.current}
      />
    </div>
  );
};

export default CareSeekerMatchmaking;
